import { buildRiskWidgetSeed } from "@repo/shared";
import type { MissingRequirementReason } from "@repo/shared/types";
import {
  usePublicInstanceRiskAssessmentValue,
  useSubmitPublicInstanceRiskAssessment,
} from "@/lib/hooks/use-public-flow/use-risk-assessment";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { RiskAssessmentWidget } from "@/pages/clients/detail/flows/instance/risk-assessment-widget";
import { T } from "./global-risk-assessment-renderer.i18n";
import type { ComponentStyleType } from "./types";

type PublicGlobalRiskAssessmentRendererProps = {
  component: { config?: string };
  riskModelId: string | null;
  flowInstanceId: string;
  isCompleted: boolean;
  language: string;
  hasValidationError?: boolean;
  /** QL-188 — group answering note; null for non-group clients. */
  groupNote?: string | null;
  /** QL-212 — the completion engine's reason for the open slot (`risk_not_scored`). */
  missingReason?: MissingRequirementReason;
};

/**
 * QL-212 (D16/D17) — the client-level half of the risk assessment, on a global
 * page of the client-facing flow.
 *
 * This is where a client with three goals states their risk tolerance ONCE and
 * answers the knowledge-and-experience questions ONCE. Which of the widget's
 * four sections actually appear is decided inside the widget from the risk
 * model's scopes; a model that captures everything per goal shows nothing here
 * at all, and such a placement can no longer be saved.
 *
 * The answer is written to the instance-level row and fanned out to every goal
 * server-side. Per D17 this page shows NO fan-out summary and no per-goal status
 * list: a goal that could not be scored surfaces where it always has, in the
 * progress panel and on its own page.
 */
export function GlobalRiskAssessmentRenderer({
  component,
  riskModelId,
  flowInstanceId,
  isCompleted,
  language,
  hasValidationError,
  groupNote,
  missingReason,
}: PublicGlobalRiskAssessmentRendererProps) {
  const t = useT(T);
  const { inputData, calculatedData, isLoading } =
    usePublicInstanceRiskAssessmentValue(riskModelId);
  const submitRisk = useSubmitPublicInstanceRiskAssessment(flowInstanceId, {
    saveFailed: t.saveFailed,
    matrixGap: t.riskAnswerNotSaved,
  });

  if (!riskModelId) {
    return (
      <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg">
        {t.riskAssessmentNotConfigured}
      </div>
    );
  }

  let riskConfig: { style?: string; title?: string; description?: string } = {};
  try {
    riskConfig = component.config ? JSON.parse(component.config) : {};
  } catch {
    // Invalid config
  }

  // Unlike the goal page, the score genuinely lives on this row: the result block
  // renders globally only when all three inputs are global, and in that
  // configuration every goal resolves the same matrix cell — so the fan-out
  // writes the client's one result here for the block to read. There is no goal
  // row to prefer.
  const {
    value: riskSnapshotValue,
    finalScore,
    isScoreCapped,
  } = buildRiskWidgetSeed(inputData, calculatedData);

  const handleChange = (value: string, isComplete: boolean) => {
    try {
      const riskData = JSON.parse(value);
      // Same autosave contract as everywhere else: partial while incomplete,
      // submit once complete, no submit button. The widget decides "complete"
      // because only it knows which sections this page rendered.
      submitRisk.mutate({ data: riskData, isComplete, lang: language });
    } catch (error) {
      log.error({ err: error }, "Failed to parse risk assessment data");
    }
  };

  if (isLoading) return null;

  return (
    <RiskAssessmentWidget
      riskModelId={riskModelId}
      goalId={null}
      pageType="global"
      value={riskSnapshotValue}
      onChange={handleChange}
      disabled={isCompleted}
      finalScore={finalScore}
      isScoreCapped={isScoreCapped}
      style={(riskConfig.style as ComponentStyleType) || "border"}
      title={riskConfig.title}
      description={riskConfig.description}
      hasValidationError={hasValidationError}
      groupNote={groupNote}
      missingReason={missingReason}
    />
  );
}
